import React from "react";

import { useTranslation } from "utils";

import { HelpFieldKey, TILLAGE_OPTIONS } from "consts";

import { Form, KlimRadioGroup } from "components";

import { HelpFieldWrapper } from "pages/app/scenario-analysis/components/HelpFieldWrapper";
import { InfoTooltipIcon } from "pages/app/scenario-analysis/components/InfoTooltipIcon";
import { CropDetailsFieldGuidance } from "pages/app/scenario-analysis/hooks/useCropDetailsGuidance";

interface PracticesSectionProps {
  focusedField: HelpFieldKey | null;
  guidance: CropDetailsFieldGuidance;
  isResidueDisabled: boolean;
  showResidueLockInfo: boolean;
}

/** Tillage, residue and irrigation management of the crop year. */
export const PracticesSection = ({ focusedField, guidance, isResidueDisabled, showResidueLockInfo }: PracticesSectionProps) => {
  const { t } = useTranslation("scenarioAnalysis");
  const { onFocus, getFieldLabel } = guidance;

  const tillageOptions = TILLAGE_OPTIONS.map(({ value, labelKey }) => ({ value, label: t(labelKey) }));

  const residueOptions = [
    { value: false, label: t("cropDetailsDrawer.residue.removed") },
    { value: true, label: t("cropDetailsDrawer.residue.incorporated") },
  ];

  const irrigationOptions = [
    { value: false, label: t("cropDetailsDrawer.irrigation.rainfed") },
    { value: true, label: t("cropDetailsDrawer.irrigation.irrigated") },
  ];

  return (
    <section className="crop-details-drawer-section-card">
      <div className="crop-details-drawer-section-card-heading">{t("cropDetailsDrawer.section.management")}</div>

      <HelpFieldWrapper
        className={`crop-details-drawer-field-group ${focusedField === "tillageId" ? "is-focused" : ""}`}
        fieldKey="tillageId"
        onFocus={onFocus}
        clickOnly
      >
        <label className="crop-details-drawer-field-label">{getFieldLabel("tillageId")}</label>
        <Form.Item name="tillageId" noStyle>
          <KlimRadioGroup options={tillageOptions} type="tertiary" size="large" optionType="button" buttonStyle="solid" />
        </Form.Item>
      </HelpFieldWrapper>

      <HelpFieldWrapper
        className={`crop-details-drawer-field-group ${focusedField === "residue" ? "is-focused" : ""}`}
        fieldKey="residue"
        onFocus={onFocus}
        clickOnly
      >
        <label className="crop-details-drawer-field-label">
          {getFieldLabel("residue")}
          {showResidueLockInfo && <InfoTooltipIcon title={t("scenarioBuilder.residueLockedLeftOnFieldTooltip")} dataTestId="crop-details-residue-lock-info" />}
        </label>
        <Form.Item name="residue" noStyle>
          <KlimRadioGroup options={residueOptions} type="tertiary" size="large" optionType="button" buttonStyle="solid" disabled={isResidueDisabled} />
        </Form.Item>
      </HelpFieldWrapper>

      <HelpFieldWrapper
        className={`crop-details-drawer-field-group ${focusedField === "irrigation" ? "is-focused" : ""}`}
        fieldKey="irrigation"
        onFocus={onFocus}
        clickOnly
      >
        <label className="crop-details-drawer-field-label">{getFieldLabel("irrigation")}</label>
        <Form.Item name="irrigation" noStyle getValueProps={(value) => ({ value: value === true })}>
          <KlimRadioGroup options={irrigationOptions} type="tertiary" size="large" optionType="button" buttonStyle="solid" />
        </Form.Item>
      </HelpFieldWrapper>
    </section>
  );
};
